import * as React from "react";

import { BlockOrdering } from "Model/BlockOrdering";
import { IBlock, IQuiltRenderData, ShapeType } from "Model/IQuiltRenderData";

import { ImageDataHelper } from "Utility/ImageDataHelper";

export type RenderCallback = (context: CanvasRenderingContext2D) => void;

export interface IProps {
    // Max W / H for the canvas. May end up being smaller, but never larger.
    readonly width: number;
    readonly height: number;

    readonly renderData: IQuiltRenderData;

    readonly renderCallback?: RenderCallback;
}

interface ISize {
    readonly width: number;
    readonly height: number;
}

function getQuiltScale(quiltSize: ISize, targetSize: ISize): number {
    return Math.min(targetSize.width / quiltSize.width, targetSize.height / quiltSize.height);
}

function getTotalQuiltSize(renderData: IQuiltRenderData): ISize {
    const rows = renderData.rows;
    const cols = renderData.columns;
    const blockSize = renderData.blockSize;

    let width = cols * blockSize;
    let height = rows * blockSize;

    width += (cols - 1) * renderData.blockPaddingWidth;
    height += (rows - 1) * renderData.blockPaddingHeight;

    width += renderData.paddingWidth * 2;
    height += renderData.paddingHeight * 2;

    return {
        width,
        height
    };
}

function getBlockIndex(row: number, col: number, renderData: IQuiltRenderData): number {
    switch (renderData.blockOrdering) {
        case BlockOrdering.Alternate:
            return ((row * renderData.columns) + col) % renderData.blocks.length;
        case BlockOrdering.Row:
            return row % renderData.blocks.length;
        case BlockOrdering.Column:
            return col % renderData.blocks.length;
    }
}

function createBlockImage(context: CanvasRenderingContext2D, block: IBlock, cellSize: number, renderData: IQuiltRenderData): ImageData {
    const imageSize = block.size * cellSize;
    const image = context.createImageData(imageSize, imageSize);
    
    if (block.backgroundColorIndex >= 0) {
        const color = renderData.blockColors[(block.backgroundColorIndex + renderData.blockColorOffset) % renderData.blockColors.length];

        ImageDataHelper.fill(image, color);
    }

    for (const shape of block.shapes) {
        const color = renderData.blockColors[(shape.colorIndex + renderData.blockColorOffset) % renderData.blockColors.length];

        switch (shape.type) {
            case ShapeType.Square:
                ImageDataHelper.drawCellSquare(image, cellSize, shape.x, shape.y, color);
                break;
            case ShapeType.TriangleNE:
                ImageDataHelper.drawCellTriangleNE(image, cellSize, shape.x, shape.y, color);
                break;
            case ShapeType.TriangleNW:
                ImageDataHelper.drawCellTriangleNW(image, cellSize, shape.x, shape.y, color);
                break;
            case ShapeType.TriangleSE:
                ImageDataHelper.drawCellTriangleSE(image, cellSize, shape.x, shape.y, color);
                break;
            case ShapeType.TriangleSW:
                ImageDataHelper.drawCellTriangleSW(image, cellSize, shape.x, shape.y, color);
                break;
        }
    }

    return image;
}

function finalizeRender(context: CanvasRenderingContext2D, renderData: IQuiltRenderData, blockImages: ImageBitmap[]) {
    const quiltSize = getTotalQuiltSize(renderData);
    const scale = getQuiltScale(quiltSize, { width: context.canvas.width, height: context.canvas.height });

    const imageW = Math.floor(scale * quiltSize.width);
    const imageH = Math.floor(scale * quiltSize.height);

    const paddingX = renderData.paddingWidth;
    const paddingY = renderData.paddingHeight;
    const blockSize = Math.ceil(renderData.blockSize * scale);

    context.fillStyle = `rgb(${renderData.backgroundColor.r}, ${renderData.backgroundColor.g}, ${renderData.backgroundColor.b})`;
    context.fillRect(0, 0, imageW, imageH);

    for (let row = 0; row < renderData.rows; ++row) {
        const y = paddingY + (row * renderData.blockPaddingHeight) + (row * renderData.blockSize);

        for (let col = 0; col < renderData.columns; ++col)
        {
            const blockIndex = getBlockIndex(row, col, renderData);
            const blockImage = blockImages[blockIndex];

            const x = paddingX + (col * renderData.blockPaddingWidth) + (col * renderData.blockSize);

            context.drawImage(
                blockImage,
                Math.floor(x * scale),
                Math.floor(y * scale),
                blockSize,
                blockSize);
        }
    }
}

function render(context: CanvasRenderingContext2D, renderData: IQuiltRenderData) {
    context.fillStyle = `rgba(0, 0, 0, 1)`;
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);

    const CellSize = 64;
    const promises: Promise<ImageBitmap>[] = [];

    for (const block of renderData.blocks) {
        const image = createBlockImage(context, block, CellSize, renderData);

        promises.push(window.createImageBitmap(image));
    }
    
    Promise.all(promises).then(bitmaps => finalizeRender(context, renderData, bitmaps));
}

export function QuiltCanvas(props: IProps): React.ReactElement {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    const { renderData, renderCallback } = props;

    const quiltSize = getTotalQuiltSize(renderData);
    const scale = getQuiltScale(quiltSize, { width: props.width, height: props.height });
    const canvasWidth = Math.floor(scale * quiltSize.width);
    const canvasHeight = Math.floor(scale * quiltSize.height);

    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas === null) {
            return;
        }

        const context = canvas.getContext("2d");
        if (context === null) {
            return;
        }

        render(context, renderData);

        if (renderCallback !== undefined) {
            // Delay so that the data in the canvas is actually rendered. Google search
            // said that this approach is the best to take.
            requestAnimationFrame(() => setTimeout(() => renderCallback(context), 33));
        }
    },
    [renderData, canvasWidth, canvasHeight, renderCallback])

    return (
        <canvas
            ref={canvasRef}
            style={{ maxWidth: canvasWidth, maxHeight: canvasHeight}}
            width={canvasWidth}
            height={canvasHeight} />
    );
}